import InteractionFooterCard from "../../organisms/interation-footer-card/index";
import ShareCardModal from "../share-card-modal";
import UserInformation from "../user-information/index";
import type { MemoInterface, WriterInterface } from "@carglassgithub/memod-sdk";
import CoverMemoCard from "@components/atoms/cover-memo-card";
import { UserAtom } from "@models/state";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  startTransition,
  useEffect,
  useState,
  useReducer,
  Dispatch,
  SetStateAction,
} from "react";

interface Props {
  memo: MemoInterface;
  memoWriter: WriterInterface;
  onShare?: () => void;
  onComment?: () => void;
  showAction?: boolean;
  ownerMemo?: boolean;
  onSave?: Dispatch<SetStateAction<MemoInterface[]>>;
  profileUser?: WriterInterface;
}

export function useCardMemo(props: Props) {
  const [likes, setLikes] = useState(props.memo.total_likes);
  const [saves, setSaves] = useState(props.memo.total_saved);
  const [showShare, toggleShowShare] = useReducer((state) => !state, false);
  const user = useAtomValue(UserAtom);
  const { memoWriter } = props;

  const router = useRouter();

  useEffect(() => {
    startTransition(() => {
      setSaves(props.memo.total_saved);
      setLikes(props.memo.total_likes);
    });

    return () =>
      startTransition(() => {
        setSaves(0);
        setLikes(0);
      });
  }, [props.memo?.id]);

  const handleSaves = (saves: number) => setSaves(saves);
  const handleLikes = (likes: number) => setLikes(likes);

  return {
    model: {
      likes,
      saves,
      user,
      displayname: memoWriter.displayname,
      showShare,
    },
    operations: {
      handleSaves,
      handleLikes,
      toggleShowShare,
      router,
    },
  };
}

export default function MemodCard(props: Props) {
  const { model, operations } = useCardMemo(props);
  const { showAction = true } = props;

  return (
    <div className="flex h-full w-[274px] flex-col gap-3 rounded-xl border border-base-grey bg-memod-header-strong">
      {model.showShare && (
        <ShareCardModal
          open
          memo={props.memo}
          writer={props.memoWriter}
          onClose={operations.toggleShowShare}
        />
      )}

      <Link
        href={`/${model.displayname}/${props.memo.slug}-${props.memo.id}`}
        className="flex w-full cursor-pointer gap-2"
      >
        <CoverMemoCard memo={props.memo} />
      </Link>

      <main
        className={classNames("flex w-full flex-col gap-1 px-3", {
          "pb-3": operations.router.pathname !== "/topics" && !showAction,
        })}
      >
        <UserInformation writer={props.memoWriter} />
        <h1 className="text-xl font-semibold text-base-white line-clamp-2">
          {props.memo?.title}
        </h1>
        <p className=" mt-2 text-lg text-[#d7d9db] line-clamp-3">
          {props.memo?.description}
        </p>
      </main>

      {showAction && (
        <span>
          <InteractionFooterCard
            {...model}
            memo={props.memo}
            onSaves={operations.handleSaves}
            onLikes={operations.handleLikes}
            toggleShowShare={operations.toggleShowShare}
            ownerMemo={props.ownerMemo}
            onSave={props.onSave}
            profileUser={props.profileUser}
          />
        </span>
      )}
    </div>
  );
}
